
import { Component, Input, OnInit } from '@angular/core';
import { InstrumentCategories, ModelStrategyInstrumentOut } from '../charts/charts';
import { InstrumentDropdownInput } from '../charts/instrument-chart-card/instrument-chart-dropdown/instrument-dropdown-input';
import { instrumentCategoryColors } from '../constants';
import { DropdownPortfolioRatios, PortfolioInstrumentOut, PortfolioInstrumentSummaryOut } from 'src/app/services/rest-api/rest-api';

@Component({
  selector: 'libero-dropdown-list',
  templateUrl: './dropdown-list.component.html',
  styleUrls: ['./dropdown-list.component.scss'],
  standalone: false
})
export class DropdownListComponent implements OnInit {
  @Input() public listData?: ModelStrategyInstrumentOut[]
  @Input() public instrumentsRatios?: PortfolioInstrumentSummaryOut | null;

  public parsedData: InstrumentDropdownInput[] = [];

  public parsedRatios: Record<number, DropdownPortfolioRatios> = {};
  public CASH_ID = 0;

  public instrumentCategoryColors = instrumentCategoryColors;

  public ngOnInit(): void {
    this.parseRatios();
    this.updateDropdownData();
  }
  
  private parseRatios(): void {
    if(this.instrumentsRatios && this.listData) {
      for (const instrument of this.listData) {
        if(instrument.instrument.cash) {
          const instrumentRatios = this.instrumentsRatios.instruments.find(instr => instr.instrument.cash);
          this.parsedRatios[this.CASH_ID] = {
            amount: instrumentRatios?.amount ?? 0,
            amountRatio: instrumentRatios?.amountRatio ?? 0,
            weight: instrument.weight,
          };
        } else {
          const instrumentRatios = this.instrumentsRatios.instruments.find(instr => instr.instrument.instrumentId === instrument.instrument.instrumentId);
          this.parsedRatios[instrument.instrument.instrumentId] = {
            amount: instrumentRatios?.amount ?? 0,
            amountRatio: instrumentRatios?.amountRatio ?? 0,
            weight: instrument.weight,
          };
        }
      }
    }
  }

  public updateDropdownData(): void {
    const dropdownData: InstrumentDropdownInput[] = [];
    this.parsedData.length = 0;

    if (this.listData) {
      const uniqueKeys = new Set(this.listData.filter(instrument => !instrument.instrument.cash).map(instrument => instrument.instrument.instrumentCategory));
      uniqueKeys.add('MONEY');

      for (const instrumentCategory of [...uniqueKeys]) {
        dropdownData.push(
          {
            categoryName: instrumentCategory,
            instrumentContent: this.getAllCategoryInstruments(instrumentCategory),
          }
        );
      }
    }

    this.parsedData = dropdownData;
  }

  private getAllCategoryInstruments(categoryKey: keyof typeof InstrumentCategories): ModelStrategyInstrumentOut[] {
    if(!this.listData) {
      return [];
    }

    const categoryInstruments = this.listData.filter(instrument => instrument.instrument.instrumentCategory === categoryKey);

    // add cash to money sublist
    if(categoryKey === 'MONEY' && categoryInstruments.findIndex(instrument => instrument.instrument.cash) === -1) {
      categoryInstruments.push(this.listData.find(instrument => instrument.instrument.cash)!);
    }

    const missingInstruments = this.getMissingInstrumentsToMerge(categoryInstruments, categoryKey);

    for (const missingInstrument of missingInstruments) {
      categoryInstruments.push({
        instrument: missingInstrument.instrument,
        weight: NaN,
      });
      this.parsedRatios[missingInstrument.instrument.instrumentId] = {
        amount: missingInstrument.amount,
        amountRatio: missingInstrument.amountRatio ?? 0,
        weight: 0,
      };
    }

    return categoryInstruments;
  }

  // second instruments source, investment desired instruments may be different from actual
  private getMissingInstrumentsToMerge(categoryInstruments: ModelStrategyInstrumentOut[], categoryKey: string): PortfolioInstrumentOut[] {
    return this.instrumentsRatios?.instruments
    .filter(instrument1 =>
      instrument1.instrument.instrumentCategory === categoryKey &&
      !categoryInstruments.some((instrument2: ModelStrategyInstrumentOut) => instrument2.instrument.instrumentId === instrument1.instrument.instrumentId)
    ) ?? []
  }
}
